import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";

type Bounds = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

type LastKnownPositionsParams = {
  bounds: Bounds;
};

type LastKnownPositionsOptions = {
  refetchIntervalMs?: number;
};

export default function useLastKnownPositions(
  params?: LastKnownPositionsParams,
  options?: LastKnownPositionsOptions,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();

  const boundsKey = useMemo(() => {
    if (!params?.bounds) return null;
    const { left, bottom, right, top } = params.bounds;
    return [left, bottom, right, top] as const;
  }, [params?.bounds]);

  return useQuery<[number, number, string, boolean][]>({
    queryKey: ["positions", activeSessionId, groups, groupFilterCondition, boundsKey],
    queryFn: async ({ signal }) => {
      const base = {
        includeDevice: true,
        includeDrivingState: true,
        ...(params ?? {}),
      };

      // A flat group selection keeps going out as `groups`, exactly as before — this is an
      // undocumented method on the map's two-second poll, so its working path is left alone.
      // Only the case that previously sent no scope at all is new: an advanced group filter
      // with no simple group selected now travels as a groupFilterCondition.
      const request =
        !groups?.length && groupFilterCondition
          ? { ...base, groupFilterCondition }
          : { ...base, groups };

      const positions = await geotabClient.call("GetLastKnownPositions", request, signal);
      return positions as [number, number, string, boolean][];
    },
    refetchInterval: options?.refetchIntervalMs ?? 2000,
    placeholderData: keepPreviousData,
  });
}
